import { faCheck, faChevronRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  ChildUser,
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  Loader,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import DoterChatFlag, { FLAG_LABEL_KEYS } from "./DoterChatFlag"
import FlaggedConvoModal from "./FlaggedConvoModal"
import useFlaggedConvoModalStore from "./useFlaggedConvoModalStore"
import NotificationDot2 from "../../../../components/notificationDot/NotificationDot2"
import { DoterChatFlagContent, QUERY_KEYS } from "../../../../constants"
import { markFlaggedChatAsRead } from "../../../../networking/chatSessions/api"
import { useFlaggedDoterChatLogsQuery } from "../../../../networking/chatSessions/queries"
import { useFamilyQuery } from "../../../../networking/queries"
import {
  AIChatFlaggedPromptNotification,
  AIPromptFlag,
  FlagType,
} from "../../../../types"
import { trackAnalyticEvent } from "../../../../util/analytics"
import { ANALYTIC_EVENTS } from "../../../../util/analytics"
import { queryClient } from "../../../../util/queryClient"
import { getChildImage, getUserFromFamily } from "../../../../util/util"
import useFamilyUserSelectStore from "../../hooks/useFamilyUserSelect"

const FlaggedConvos = ({ type }: { type: FlagType }) => {
  const flags = Object.values(AIPromptFlag)
  const { t } = useTranslation()
  return (
    <FlexBox
      direction="column"
      wrap={false}
      gap={SPACING.space4}
      style={{ overflowY: "auto" }}
    >
      {type === FlagType.RED && (
        <Typography variant="bodySmall">
          {t("DC_FLAGGED_PREFIX")}
          {flags.map((flag, index) => (
            <span
              key={flag}
              style={{
                color: DoterChatFlagContent[flag].color,
                fontWeight: 500,
              }}
            >
              {t(FLAG_LABEL_KEYS[flag])}
              {index < flags.length - 2 ? ", " : ""}
              {index === flags.length - 2 ? t("DC_LIST_AND") : ""}
            </span>
          ))}
          {t("DC_FLAGGED_SUFFIX")}
        </Typography>
      )}
      <FlaggedConvosList type={type} />
      <FlaggedConvoModal />
    </FlexBox>
  )
}

export default FlaggedConvos

const FlaggedConvosList = ({ type }: { type: FlagType }) => {
  const { data: convos, isLoading } = useFlaggedDoterChatLogsQuery(type)
  const { selectedUserId } = useFamilyUserSelectStore()
  const { t } = useTranslation()

  const filteredConvos =
    convos?.filter((convo) => convo.user_id === selectedUserId) || []
  const unreadConvos = filteredConvos?.filter((convo) => !convo.date_read)

  if (isLoading)
    return (
      <FlexBox
        justify="center"
        align="center"
        style={{ padding: SPACING.space6 }}
      >
        <Loader color={JoonUIColor.icon.neutral} thickness={2} />
      </FlexBox>
    )

  if (!filteredConvos) return null

  if (filteredConvos.length === 0)
    return (
      <FlexBox justify="center" align="center">
        <Typography variant="bodySmall">{t("DC_NO_CONVOS")}</Typography>
      </FlexBox>
    )

  return (
    <FlexBox direction="column" gap={SPACING.space2}>
      {unreadConvos?.length > 0 && (
        <FlexBox
          direction="row"
          gap={SPACING.space2}
          justify="space-between"
          align="center"
        >
          <Typography variant="bodySmall">
            {t("DC_N_FLAGGED", { count: unreadConvos?.length })}
          </Typography>
          <MarkAllAsReadButton type={type} />
        </FlexBox>
      )}
      {filteredConvos.map((log) => (
        <FlaggedConvosListItem key={log.id} log={log} />
      ))}
    </FlexBox>
  )
}

const MarkAllAsReadButton = ({ type }: { type: FlagType }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { data: convos, refetch } = useFlaggedDoterChatLogsQuery(type)
  const { selectedUserId } = useFamilyUserSelectStore()
  const { t } = useTranslation()
  const filteredConvos = convos?.filter(
    (convo) => convo.user_id === selectedUserId
  )

  const handleMarkAllAsRead = async () => {
    setIsLoading(true)
    const ids = filteredConvos?.map((convo) => convo.id)
    const promises: Promise<any>[] = []
    ids?.forEach((id) => promises.push(markFlaggedChatAsRead(id)))
    await Promise.all(promises)
    queryClient.invalidateQueries([QUERY_KEYS.FLAGGED_DOTER_CHAT_LOGS])
    refetch()
    setIsLoading(false)
    trackAnalyticEvent(ANALYTIC_EVENTS.MARK_ALL_FLAGGED_MESSAGES_READ)
  }

  return (
    // TODO: update this to small subtle button once variant is built
    <button
      style={{
        padding: "4px 6px",
        border: `1px solid ${JoonUIColor.border.default}`,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isLoading ? "not-allowed" : "pointer",
        gap: SPACING.space1,
        background:
          isHovered || isLoading
            ? JoonUIColor.background.xlightGray
            : JoonUIColor.background.primaryNeutral,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isLoading}
      onClick={handleMarkAllAsRead}
    >
      <div style={{ position: "absolute", opacity: isLoading ? 1 : 0 }}>
        <Loader thickness={2} size={16} color={JoonUIColor.icon.neutral} />
      </div>
      <FlexBox
        direction="row"
        align="center"
        gap={SPACING.space1}
        style={{ opacity: isLoading ? 0 : 1 }}
      >
        <FontAwesomeIcon icon={faCheck} />
        <Typography variant="bodySmall">{t("DC_MARK_ALL_READ")}</Typography>
      </FlexBox>
    </button>
  )
}

const FlaggedConvosListItem = ({
  log,
}: {
  log: AIChatFlaggedPromptNotification
}) => {
  const { refetch } = useFlaggedDoterChatLogsQuery()
  const { onOpen: openFlaggedConvoModal } = useFlaggedConvoModalStore()
  const { data: family } = useFamilyQuery()
  const { t } = useTranslation()
  const child = getUserFromFamily(family!, log.user_id) as ChildUser | undefined

  if (!child) return null

  const doter = child.active_doter
  const doterImage = getChildImage(child)
  const isRedFlag = log.type === FlagType.RED

  const handleReadConvo = async () => {
    try {
      const formattedDate = dayjs(log.date_created).format("YYYY-MM-D")
      openFlaggedConvoModal(formattedDate, log.message.id)
      await markFlaggedChatAsRead(log.id)
      queryClient.invalidateQueries([QUERY_KEYS.FLAGGED_DOTER_CHAT_LOGS])
      refetch()
      trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_FLAGGED_CONVERSATION)
    } catch (error) {
      console.error("error marking convo as read", error)
    }
  }

  return (
    <FlexBox
      id={log.id.toString()}
      direction="row"
      style={{
        border: `1px solid ${JoonUIColor.border.default}`,
        borderRadius: SPACING.space2,
        overflow: "hidden",
      }}
    >
      <FlexBox
        direction="row"
        align="center"
        wrap={false}
        gap={SPACING.space2}
        justify="space-between"
        style={{
          background: JoonUIColor.background.lightGray,
          padding: SPACING.space2,
          borderBottom: isRedFlag
            ? "none"
            : `1px solid ${JoonUIColor.border.default}`,
        }}
      >
        <FlexBox direction="row" align="center" gap={SPACING.space1}>
          {!log.date_read && <NotificationDot2 />}
          <Typography variant="bodySmall" style={{ fontWeight: 500 }}>
            {dayjs(log.date_created).format("MMM D")}
          </Typography>
        </FlexBox>
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: SPACING.space1,
          }}
          onClick={handleReadConvo}
        >
          <Typography variant="bodyXSmall" style={{ whiteSpace: "nowrap" }}>
            {t("DC_READ_CONVERSATION")}
          </Typography>
          <FontAwesomeIcon
            icon={faChevronRight}
            color={JoonUIColor.icon.neutral}
          />
        </button>
      </FlexBox>

      {isRedFlag ? (
        <FlexBox
          direction="column"
          gap={SPACING.space1}
          style={{
            padding: SPACING.space2,
            backgroundColor: JoonUIColor.background.primaryNeutral,
          }}
        >
          <FlexBox
            direction="column"
            gap={SPACING.space1}
            style={{
              background: JoonColorExpanded.purple600,
              padding: "6px 12px",
              borderRadius: SPACING.space2,
            }}
          >
            <Typography variant="bodySmall" color={JoonUIColor.text.inverted}>
              {log.message.prompt}
            </Typography>
            <FlexBox direction="row" gap={SPACING.space1}>
              {log.message.prompt_flags &&
                log.message.prompt_flags.map((flag) => (
                  <DoterChatFlag key={flag} type={flag} />
                ))}
            </FlexBox>
          </FlexBox>
        </FlexBox>
      ) : (
        <>
          <div
            className="flex flex-col gap-1 p-2"
            style={{
              backgroundColor: JoonUIColor.background.lightGray,
              borderBottom: `1px solid ${JoonUIColor.border.default}`,
            }}
          >
            <Typography variant="bodySmall">
              {log.message.positive_flag_summary?.summary}
            </Typography>
            <FlexBox direction="row" gap={SPACING.space1}>
              {log.message.positive_flag_summary?.flags &&
                log.message.positive_flag_summary.flags.map((flag) => (
                  <DoterChatFlag key={flag} type={flag} />
                ))}
            </FlexBox>
          </div>
          <div
            className="flex flex-col gap-1 p-2 w-full"
            style={{
              backgroundColor: JoonUIColor.background.primaryNeutral,
            }}
          >
            <FlexBox
              direction="row"
              gap={SPACING.space2}
              align="center"
              style={{ width: "fit-content" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: JoonColorExpanded.purple600,
                  width: SPACING.space6,
                  height: SPACING.space6,
                  borderRadius: "50%",
                }}
              >
                <Typography
                  variant="bodyXSmall"
                  color={JoonUIColor.text.inverted}
                >
                  {child?.name[0].toUpperCase()}
                </Typography>
              </div>
              <Typography variant="bodySmallBold">{child.name}</Typography>
            </FlexBox>
            <Typography variant="bodySmall">{log.message.prompt}</Typography>
            <FlexBox
              direction="row"
              gap={SPACING.space2}
              style={{ width: "fit-content" }}
              align="center"
            >
              <img
                src={doterImage}
                alt={doter?.name}
                style={{
                  width: SPACING.space6,
                  height: SPACING.space6,
                  borderRadius: "50%",
                }}
              />
              <Typography variant="bodySmallBold">
                {t("DC_DOTER_LABEL", { name: doter?.name })}
              </Typography>
            </FlexBox>
            <FlexBox direction="row" gap={SPACING.space1}>
              {log.message.responses.map((response) => (
                <Typography variant="bodySmall">{response}</Typography>
              ))}
            </FlexBox>
          </div>
        </>
      )}
    </FlexBox>
  )
}
